import { useCallback, useEffect, useState } from 'react';
import { ShieldAlert, CheckCircle2 } from 'lucide-react';
import { api } from '../api';
import ListaEstandar from '../ui/ListaEstandar';
import ChipEstado from '../ui/ChipEstado';
import EstadoVacio from '../ui/EstadoVacio';
import TiempoRelativo from '../ui/TiempoRelativo';
import NotaExplicativa from '../ui/NotaExplicativa';
import ModalConfirmar from '../ui/ModalConfirmar';
import { SelectFiltro } from '../ui/Filtros';
import { SEVERIDAD_ALERTA } from '../ui/estados';

// Qué significa cada tipo, dicho para una persona y no para un log.
const TIPOS = {
  FUERA_HORARIO: {
    etiqueta: 'Fuera de horario',
    explica: 'Alguien movió inventario fuera del turno configurado.',
  },
  RAFAGA: {
    etiqueta: 'Ráfaga de movimientos',
    explica: 'Muchos movimientos del mismo usuario en muy pocos minutos.',
  },
  SALIDA_REPETIDA_ALTO_VALOR: {
    etiqueta: 'Salidas repetidas',
    explica: 'Salidas pequeñas y repetidas del mismo producto que, sumadas, superan el umbral. Ninguna por separado pedía autorización.',
  },
  PERMISO_DENEGADO: {
    etiqueta: 'Intento sin permiso',
    explica: 'Alguien intentó una acción que su rol no permite. Repetirlo es explorar, no equivocarse.',
  },
  LOGIN_FALLIDO: {
    etiqueta: 'Intentos de acceso',
    explica: 'Varios intentos fallidos de contraseña sobre una cuenta real.',
  },
};

export default function Anomalias() {
  const [atendidas, setAtendidas] = useState('abiertas');
  const [alertas, setAlertas] = useState([]);
  const [cargando, setCargando] = useState(true);
  const [error, setError] = useState(null);
  const [atender, setAtender] = useState(null);

  const consulta = useCallback(
    (filtro) => `/alertas?atendidas=${filtro === 'todas'}`,
    [],
  );

  const cargar = useCallback((filtro) => {
    setCargando(true);
    return api.get(consulta(filtro))
      .then(setAlertas)
      .catch((e) => setError(e.message))
      .finally(() => setCargando(false));
  }, [consulta]);

  useEffect(() => {
    let vigente = true;
    api.get(consulta(atendidas))
      .then((d) => vigente && setAlertas(d))
      .catch((e) => vigente && setError(e.message))
      .finally(() => vigente && setCargando(false));
    return () => { vigente = false; };
  }, [atendidas, consulta]);

  const columnas = [
    {
      etiqueta: 'Qué pasó', valor: (a) => a.titulo, clase: 'font-medium text-gray-900',
      render: (a) => (
        <span>
          <span className="block">{a.titulo}</span>
          <span className="block text-xs text-gray-500">{TIPOS[a.tipo]?.explica}</span>
        </span>
      ),
    },
    { etiqueta: 'Tipo', valor: (a) => TIPOS[a.tipo]?.etiqueta || a.tipo, clase: 'text-gray-600' },
    {
      etiqueta: 'Veces', alineado: 'derecha', valor: (a) => a.veces,
      render: (a) => (
        <span className={`tabular-nums font-semibold ${a.veces > 1 ? 'text-red-700' : 'text-gray-600'}`}>
          {a.veces}
        </span>
      ),
    },
    { etiqueta: 'Quién', valor: (a) => a.usuario || '—' },
    { etiqueta: 'Última vez', tipo: 'fecha_hora', valor: (a) => a.ultima_vez, render: (a) => <TiempoRelativo valor={a.ultima_vez} /> },
    {
      etiqueta: 'Gravedad', valor: (a) => a.severidad,
      render: (a) => <ChipEstado mapa={SEVERIDAD_ALERTA} clave={a.severidad} />,
    },
  ];

  return (
    <>
      <ListaEstandar
        titulo="Movimientos raros"
        subtitulo="Patrones raros en la actividad. No son errores del sistema: son preguntas sobre lo que está pasando."
        nota={
          <NotaExplicativa titulo="¿En qué se diferencia esto de las alertas de stock?">
            Un aviso de stock bajo es <strong>trabajo</strong>: hay que reponer. Una anomalía es una
            <strong> pregunta sobre alguien</strong>: por qué se movió inventario de madrugada, por qué
            este usuario sacó el mismo producto cinco veces hoy. Viven en listas separadas —
            juntas, la segunda se pierde entre las primeras justo cuando más importa verla.
            Atender una alerta no la borra: la cierra con tu explicación, y si el patrón se repite se
            abre una nueva.
          </NotaExplicativa>
        }
        filtros={
          <SelectFiltro
            valor={atendidas}
            onChange={setAtendidas}
            opciones={[
              { valor: 'abiertas', etiqueta: 'Sin revisar' },
              { valor: 'todas', etiqueta: 'Incluir revisadas' },
            ]}
          />
        }
        columnas={columnas}
        filas={alertas}
        cargando={cargando}
        error={error}
        onReintentar={() => { setError(null); cargar(atendidas); }}
        exportar={{ nombre: 'anomalias', columnas }}
        vacio={
          <EstadoVacio
            icono={CheckCircle2}
            titulo="Nada raro que revisar"
            descripcion="Si acabas de encender las anomalías, ten en cuenta que solo se evalúan los movimientos nuevos: no se revisa el pasado hacia atrás."
          />
        }
        acciones={(a) => (a.atendida ? [] : [{ etiqueta: 'Marcar revisada', onClick: () => setAtender(a) }])}
      />

      {/* El fallo de carga lo pinta la lista (motivo en español + reintentar). */}

      {atender && (
        <ModalConfirmar
          titulo="Marcar la alerta como revisada"
          descripcion={atender.titulo}
          extra={<Detalle a={atender} />}
          aviso="No se borra: queda cerrada con tu explicación. Si el patrón vuelve a ocurrir, se abrirá una alerta nueva."
          pedirMotivo
          etiquetaConfirmar="Marcar revisada"
          onConfirmar={async (motivo) => {
            await api.post(`/alertas/${atender.id}/atender`, { motivo });
            setAtender(null);
            cargar(atendidas);
          }}
          onCerrar={() => setAtender(null)}
        />
      )}
    </>
  );
}

function Detalle({ a }) {
  const entradas = Object.entries(a.detalle || {}).filter(([, v]) => v != null);
  return (
    <div className="space-y-2">
      <p className="text-sm text-gray-600">{TIPOS[a.tipo]?.explica}</p>
      {entradas.length > 0 && (
        <div className="border border-gray-200 rounded-lg divide-y divide-gray-100 text-sm">
          {entradas.map(([k, v]) => (
            <div key={k} className="flex justify-between gap-3 px-3 py-1.5">
              <span className="text-gray-500">{k.replace(/_/g, ' ')}</span>
              <span className="text-gray-900 text-right">{String(v)}</span>
            </div>
          ))}
        </div>
      )}
      <p className="text-xs text-gray-500">
        Ocurrió {a.veces} vez/veces. Primera: <ShieldAlert className="inline h-3 w-3" />{' '}
        <TiempoRelativo valor={a.primera_vez} />.
      </p>
    </div>
  );
}
